import { Text, TextColor, TextVariant } from '@vizit/atoms';
import { CheckSquareFilledIcon, ErrorSquareFilledIcon, type IconProps, RobotIcon, Tablet01Icon } from '@vizit/icons';
import type { ComponentType } from 'react';

type LeadingIcon = ComponentType<IconProps>;

interface HeroScoringReadinessBadgeProps {
  label: string;
  isReady: boolean;
  LeadingIcon: LeadingIcon;
  readyLabel: string;
  notReadyLabel: string;
}

function HeroScoringReadinessBadge({
  label,
  isReady,
  LeadingIcon,
  readyLabel,
  notReadyLabel,
}: HeroScoringReadinessBadgeProps) {
  const TrailingIcon = isReady ? CheckSquareFilledIcon : ErrorSquareFilledIcon;
  const trailingClassName = isReady ? 'text-success-secondary' : 'text-error-primary';
  const trailingLabel = isReady ? readyLabel : notReadyLabel;

  return (
    <div
      className="flex flex-1 items-center gap-4 self-stretch rounded-[1px] bg-secondary p-4"
      data-testid={`hero-scoring-readiness-badge-${isReady ? 'ready' : 'not-ready'}`}
    >
      <LeadingIcon size={18} className="shrink-0 text-secondary" />
      <Text
        variant={TextVariant.SmMedium}
        color={TextColor.Secondary}
        showEllipsis={false}
        elementType="span"
        className="flex-1"
      >
        {label}
      </Text>
      <TrailingIcon size={20} aria-label={trailingLabel} className={`shrink-0 ${trailingClassName}`} />
    </div>
  );
}

interface HeroScoringReadinessBadgesProps {
  agentReady: boolean;
  mobileReady: boolean;
}

function HeroScoringReadinessBadges({ agentReady, mobileReady }: HeroScoringReadinessBadgesProps) {
  return (
    <div className="flex items-center gap-2 self-stretch">
      <HeroScoringReadinessBadge
        label="Agent-ready"
        isReady={agentReady}
        LeadingIcon={RobotIcon}
        readyLabel="Agent ready"
        notReadyLabel="Agent not ready"
      />
      <HeroScoringReadinessBadge
        label="Mobile-ready"
        isReady={mobileReady}
        LeadingIcon={Tablet01Icon}
        readyLabel="Mobile ready"
        notReadyLabel="Mobile not ready"
      />
    </div>
  );
}

export { HeroScoringReadinessBadges };
export type { HeroScoringReadinessBadgesProps };
